import './TreePicker.scss'
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type KeyboardEvent,
  type ReactNode
} from 'react'
import {
  MdOutlineCheck,
  MdOutlineChevronRight,
  MdOutlineClose,
  MdOutlineFolder
} from 'react-icons/md'
import { Button } from '../Button'
import { clx } from '../clx'
import { ancestorsOf, branchIds, filterTree, type TreeNode } from './filterTree'

export interface TreePickerProps extends Omit<
  ComponentProps<'ul'>,
  'onChange' | 'children' | 'defaultValue' | 'role'
> {
  nodes: readonly TreeNode[]
  /** Selected node id. Controlled: a dialog keeps it as its pending choice and commits on save. */
  value: string | null
  onChange: (id: string) => void
  /** Show the search field. */
  searchable?: boolean
  /** Focus the search field on mount (the portal's folder modal does). */
  autoFocusSearch?: boolean
  searchLabel?: string
  searchPlaceholder?: string
  /** Shown when nothing matches the search, or there are no nodes. */
  emptyText?: ReactNode
  /** Initially expand every branch (portal parity) or only the path to the selection. */
  defaultExpanded?: 'all' | 'selected'
  className?: string
}

interface VisibleNode {
  node: TreeNode
  parentId: string | null
  domId: string
}

/**
 * Single-select WAI-ARIA tree (folder pickers). Focus stays on the tree and
 * the active item is announced through aria-activedescendant. It has no inner
 * scroller: the dialog body scrolls it.
 */
export function TreePicker({
  nodes,
  value,
  onChange,
  searchable = false,
  autoFocusSearch = false,
  searchLabel = 'Search folders',
  searchPlaceholder = 'Search folders..',
  emptyText = 'No folders found',
  defaultExpanded = 'all',
  className,
  ref,
  ...rest
}: TreePickerProps) {
  const base = `tree-picker-${useId()}`
  const treeRef = useRef<HTMLUListElement | null>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const typeahead = useRef({ text: '', at: 0 })

  const initialExpanded = (list: readonly TreeNode[]) =>
    defaultExpanded === 'all'
      ? branchIds(list)
      : new Set(value === null ? [] : ancestorsOf(list, value))

  const [query, setQuery] = useState('')
  const [expanded, setExpanded] = useState<Set<string>>(() => initialExpanded(nodes))
  const [active, setActive] = useState<string | null>(null)

  // Nodes often arrive after mount (a fetch): re-derive the expansion then.
  const [seenNodes, setSeenNodes] = useState(nodes)
  if (seenNodes !== nodes) {
    setSeenNodes(nodes)
    setExpanded(query ? branchIds(filterTree(nodes, query)) : initialExpanded(nodes))
  }

  const filtered = useMemo(() => filterTree(nodes, query), [nodes, query])

  const visible = useMemo(() => {
    const out: VisibleNode[] = []
    const walk = (list: readonly TreeNode[], parentId: string | null) => {
      for (const node of list) {
        out.push({ node, parentId, domId: `${base}-item-${out.length}` })
        if (node.children?.length && expanded.has(node.id)) walk(node.children, node.id)
      }
    }
    walk(filtered, null)
    return out
  }, [filtered, expanded, base])

  const indexOf = useMemo(() => new Map(visible.map((v, i) => [v.node.id, i])), [visible])

  const activeIndex =
    (active !== null ? indexOf.get(active) : undefined) ??
    (value !== null ? indexOf.get(value) : undefined) ??
    (visible.length ? 0 : -1)
  const activeItem = visible[activeIndex]

  useEffect(() => {
    if (autoFocusSearch) searchRef.current?.focus()
  }, [autoFocusSearch])

  useEffect(() => {
    if (!activeItem || !treeRef.current?.contains(document.activeElement)) return
    document
      .getElementById(activeItem.domId)
      ?.firstElementChild?.scrollIntoView({ block: 'nearest' })
  }, [activeItem])

  const search = (next: string) => {
    setQuery(next)
    setActive(null)
    setExpanded(next.trim() ? branchIds(filterTree(nodes, next)) : initialExpanded(nodes))
  }

  const setOpen = (id: string, isOpen: boolean) => {
    setExpanded((prev) => {
      if (prev.has(id) === isOpen) return prev
      const next = new Set(prev)
      if (isOpen) next.add(id)
      else next.delete(id)
      return next
    })
  }

  const focusAt = (i: number) => {
    const target = visible[Math.max(0, Math.min(visible.length - 1, i))]
    if (target) setActive(target.node.id)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    if (!activeItem) return
    const { node } = activeItem
    const hasChildren = Boolean(node.children?.length)
    const isOpen = expanded.has(node.id)
    const handled = () => {
      event.preventDefault()
    }
    switch (event.key) {
      case 'ArrowDown':
        handled()
        focusAt(activeIndex + 1)
        return
      case 'ArrowUp':
        handled()
        focusAt(activeIndex - 1)
        return
      case 'Home':
        handled()
        focusAt(0)
        return
      case 'End':
        handled()
        focusAt(visible.length - 1)
        return
      case 'ArrowRight':
        handled()
        if (hasChildren && !isOpen) setOpen(node.id, true)
        else if (hasChildren) focusAt(activeIndex + 1)
        return
      case 'ArrowLeft':
        handled()
        if (hasChildren && isOpen) setOpen(node.id, false)
        else if (activeItem.parentId !== null) setActive(activeItem.parentId)
        return
      case 'Enter':
      case ' ':
        handled()
        onChange(node.id)
        return
      default:
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
          handled()
          typeAhead(event.key)
        }
    }
  }

  // Type-ahead: jump to the next visible item whose label starts with the typed text.
  const typeAhead = (key: string) => {
    const now = Date.now()
    const state = typeahead.current
    state.text = now - state.at > 500 ? key.toLowerCase() : state.text + key.toLowerCase()
    state.at = now
    const count = visible.length
    const start = state.text.length > 1 ? activeIndex : activeIndex + 1
    for (let step = 0; step < count; step++) {
      const candidate = visible[(start + step) % count]
      if (candidate?.node.label.toLowerCase().startsWith(state.text)) {
        setActive(candidate.node.id)
        return
      }
    }
  }

  const domIdOf = (id: string) => {
    const i = indexOf.get(id)
    return i === undefined ? undefined : visible[i]?.domId
  }

  const renderNodes = (list: readonly TreeNode[]): ReactNode =>
    list.map((node) => {
      const hasChildren = Boolean(node.children?.length)
      const isOpen = hasChildren && expanded.has(node.id)
      const isSelected = node.id === value
      return (
        <li
          key={node.id}
          id={domIdOf(node.id)}
          role="treeitem"
          aria-selected={isSelected}
          aria-expanded={hasChildren ? isOpen : undefined}
          data-active={activeItem?.node.id === node.id || undefined}
          className="tree-picker__item"
        >
          <div
            className="tree-picker__row"
            onMouseDown={(event) => {
              event.preventDefault()
            }}
            onClick={() => {
              setActive(node.id)
              onChange(node.id)
              treeRef.current?.focus()
            }}
          >
            <span
              className="tree-picker__toggle"
              aria-hidden="true"
              onClick={(event) => {
                if (!hasChildren) return
                event.stopPropagation()
                setActive(node.id)
                setOpen(node.id, !isOpen)
                treeRef.current?.focus()
              }}
            >
              {hasChildren && <MdOutlineChevronRight />}
            </span>
            <span className="tree-picker__icon" aria-hidden="true">
              {isSelected ? <MdOutlineCheck /> : <MdOutlineFolder />}
            </span>
            <span className="tree-picker__label">{node.label}</span>
          </div>
          {isOpen && node.children && (
            <ul role="group" className="tree-picker__group">
              {renderNodes(node.children)}
            </ul>
          )}
        </li>
      )
    })

  return (
    <div className={clx('tree-picker', searchable && 'tree-picker--has-search', className)}>
      {searchable && (
        <div className="tree-picker__search">
          <input
            ref={searchRef}
            type="search"
            className="tree-picker__search-input"
            aria-label={searchLabel}
            aria-controls={`${base}-tree`}
            placeholder={searchPlaceholder}
            value={query}
            onChange={(event) => {
              search(event.target.value)
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown' && visible.length) {
                event.preventDefault()
                treeRef.current?.focus()
              }
            }}
          />
          {query && (
            <Button
              size="sm"
              variant="ghost"
              icon={<MdOutlineClose />}
              onClick={() => {
                search('')
                searchRef.current?.focus()
              }}
            >
              Clear
            </Button>
          )}
        </div>
      )}
      {visible.length === 0 ? (
        <p className="tree-picker__empty" role="status">
          {emptyText}
        </p>
      ) : (
        <ul
          id={`${base}-tree`}
          tabIndex={0}
          {...rest}
          ref={(node) => {
            treeRef.current = node
            if (typeof ref === 'function') ref(node)
            else if (ref) ref.current = node
          }}
          role="tree"
          aria-activedescendant={activeItem?.domId}
          className="tree-picker__tree"
          onKeyDown={onKeyDown}
        >
          {renderNodes(filtered)}
        </ul>
      )}
    </div>
  )
}
